import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { TestimonialsLayoutId, SectionLayoutConfig } from '@/constants/layoutTypes';

interface TestimonialsSectionProps {
    testimonials: Array<{ text: string; author: string; role: string }>;
    title?: string;
    primaryColor: string;
    isEditing: boolean;
    onEdit: (section: string, field: string, value: string, index?: number) => void;
    sizeClasses: { heading: string; subheading: string; body: string; small: string };
    layout?: TestimonialsLayoutId;
    layoutConfig?: SectionLayoutConfig;
}

// Shared star rating component
function StarRating() {
    return (
        <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
                <Icon key={i} name="StarIcon" className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            ))}
        </div>
    );
}

// Shared avatar component
function Avatar({ name, primaryColor, size = 'md' }: { name: string; primaryColor: string; size?: 'sm' | 'md' | 'lg' }) {
    const sizeClass = size === 'lg' ? 'w-16 h-16 text-xl' : size === 'sm' ? 'w-10 h-10 text-sm' : 'w-12 h-12 text-lg';
    return (
        <div
            className={`${sizeClass} rounded-full flex items-center justify-center text-white font-bold`}
            style={{ backgroundColor: primaryColor }}
        >
            {name.charAt(0)}
        </div>
    );
}

// ============================================================================
// LAYOUT 1: CARDS GRID (Default)
// ============================================================================
function TestimonialsCards({
    testimonials, title, primaryColor, isEditing, onEdit, sizeClasses
}: Omit<TestimonialsSectionProps, 'layout' | 'layoutConfig'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <section id="testimonials" className="py-20 px-6 bg-gray-50 @container">
            <div className="max-w-6xl mx-auto">
                <h2
                    className={`${sizeClasses.heading} font-bold text-center text-gray-900 mb-12 ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('testimonials', 'title', e.currentTarget.textContent || '')}
                >
                    {title || 'What Founders Say'}
                </h2>
                <div className="grid @md:grid-cols-2 gap-8">
                    {testimonials.map((testimonial, idx) => (
                        <div key={idx} className="p-8 bg-white rounded-2xl border border-gray-200 shadow-sm">
                            <div className="mb-4">
                                <StarRating />
                            </div>
                            <p
                                className={`${sizeClasses.body} text-gray-700 mb-6 italic leading-relaxed ${editClass}`}
                                contentEditable={isEditing}
                                suppressContentEditableWarning
                                onBlur={(e) => onEdit('testimonials', 'text', e.currentTarget.textContent?.replace(/^"|"$/g, '') || '', idx)}
                            >
                                &ldquo;{testimonial.text}&rdquo;
                            </p>
                            <div className="flex items-center gap-3">
                                <Avatar name={testimonial.author} primaryColor={primaryColor} />
                                <div>
                                    <p
                                        className={`${sizeClasses.body} font-semibold text-gray-900 ${editClass}`}
                                        contentEditable={isEditing}
                                        suppressContentEditableWarning
                                        onBlur={(e) => onEdit('testimonials', 'author', e.currentTarget.textContent || '', idx)}
                                    >
                                        {testimonial.author}
                                    </p>
                                    <p
                                        className={`${sizeClasses.small} text-gray-600 ${editClass}`}
                                        contentEditable={isEditing}
                                        suppressContentEditableWarning
                                        onBlur={(e) => onEdit('testimonials', 'role', e.currentTarget.textContent || '', idx)}
                                    >
                                        {testimonial.role}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 2: QUOTE SPOTLIGHT
// ============================================================================
function TestimonialsSpotlight({
    testimonials, title, primaryColor, isEditing, onEdit, sizeClasses
}: Omit<TestimonialsSectionProps, 'layout' | 'layoutConfig'>) {
    const [activeIndex, setActiveIndex] = useState(0);
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';
    const current = testimonials[activeIndex] || testimonials[0];

    if (!current) return null;

    return (
        <section id="testimonials" className="py-24 px-6 bg-white @container">
            <div className="max-w-4xl mx-auto text-center">
                <h2
                    className={`${sizeClasses.heading} font-bold text-gray-900 mb-16 ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('testimonials', 'title', e.currentTarget.textContent || '')}
                >
                    {title || 'What Founders Say'}
                </h2>

                {/* Large quote icon */}
                <div className="mb-8">
                    <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto"
                        style={{ backgroundColor: `${primaryColor}15` }}
                    >
                        <svg className="w-7 h-7" style={{ color: primaryColor }} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                        </svg>
                    </div>
                </div>

                {/* Featured Quote */}
                <p
                    className={`text-2xl @md:text-3xl @lg:text-4xl font-medium text-gray-900 leading-relaxed mb-10 ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('testimonials', 'text', e.currentTarget.textContent?.replace(/^"|"$/g, '') || '', activeIndex)}
                >
                    &ldquo;{current.text}&rdquo;
                </p>

                {/* Author */}
                <div className="flex flex-col items-center gap-3 mb-10">
                    <Avatar name={current.author} primaryColor={primaryColor} size="lg" />
                    <div>
                        <p
                            className={`text-lg font-semibold text-gray-900 ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('testimonials', 'author', e.currentTarget.textContent || '', activeIndex)}
                        >
                            {current.author}
                        </p>
                        <p
                            className={`${sizeClasses.body} text-gray-500 ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('testimonials', 'role', e.currentTarget.textContent || '', activeIndex)}
                        >
                            {current.role}
                        </p>
                    </div>
                    <div className="mt-1">
                        <StarRating />
                    </div>
                </div>

                {/* Navigation Dots */}
                {testimonials.length > 1 && (
                    <div className="flex items-center justify-center gap-2">
                        {testimonials.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setActiveIndex(idx)}
                                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                    idx === activeIndex ? 'scale-125' : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                                style={idx === activeIndex ? { backgroundColor: primaryColor } : {}}
                                aria-label={`View testimonial ${idx + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 3: CAROUSEL
// ============================================================================
function TestimonialsCarousel({
    testimonials, title, primaryColor, isEditing, onEdit, sizeClasses
}: Omit<TestimonialsSectionProps, 'layout' | 'layoutConfig'>) {
    const [activeIndex, setActiveIndex] = useState(0);
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    const goToPrev = () => {
        setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
    };
    const goToNext = () => {
        setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
    };

    if (testimonials.length === 0) return null;

    return (
        <section id="testimonials" className="py-20 px-6 bg-gray-50 @container">
            <div className="max-w-5xl mx-auto">
                <h2
                    className={`${sizeClasses.heading} font-bold text-center text-gray-900 mb-14 ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('testimonials', 'title', e.currentTarget.textContent || '')}
                >
                    {title || 'What Founders Say'}
                </h2>

                <div className="relative">
                    {/* Navigation Arrows */}
                    {testimonials.length > 1 && (
                        <>
                            <button
                                onClick={goToPrev}
                                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 @lg:-translate-x-6 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:shadow-xl transition-all group"
                                aria-label="Previous testimonial"
                            >
                                <Icon name="ChevronLeftIcon" className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
                            </button>
                            <button
                                onClick={goToNext}
                                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 @lg:translate-x-6 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center hover:shadow-xl transition-all group"
                                aria-label="Next testimonial"
                            >
                                <Icon name="ChevronRightIcon" className="w-5 h-5 text-gray-600 group-hover:text-gray-900" />
                            </button>
                        </>
                    )}

                    {/* Card */}
                    <div className="overflow-hidden px-4">
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                        >
                            {testimonials.map((testimonial, idx) => (
                                <div
                                    key={idx}
                                    className="w-full flex-shrink-0 px-4"
                                >
                                    <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-10 @md:p-14 max-w-3xl mx-auto">
                                        <div className="flex flex-col @md:flex-row @md:items-start gap-8">
                                            {/* Avatar + Info */}
                                            <div className="flex flex-col items-center @md:items-start shrink-0">
                                                <Avatar name={testimonial.author} primaryColor={primaryColor} size="lg" />
                                                <p
                                                    className={`text-lg font-semibold text-gray-900 mt-3 ${editClass}`}
                                                    contentEditable={isEditing}
                                                    suppressContentEditableWarning
                                                    onBlur={(e) => onEdit('testimonials', 'author', e.currentTarget.textContent || '', idx)}
                                                >
                                                    {testimonial.author}
                                                </p>
                                                <p
                                                    className={`${sizeClasses.small} text-gray-500 ${editClass}`}
                                                    contentEditable={isEditing}
                                                    suppressContentEditableWarning
                                                    onBlur={(e) => onEdit('testimonials', 'role', e.currentTarget.textContent || '', idx)}
                                                >
                                                    {testimonial.role}
                                                </p>
                                                <div className="mt-2">
                                                    <StarRating />
                                                </div>
                                            </div>

                                            {/* Quote */}
                                            <div className="flex-1">
                                                <svg className="w-8 h-8 mb-4 opacity-20" style={{ color: primaryColor }} fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                                                </svg>
                                                <p
                                                    className={`text-xl @md:text-2xl text-gray-700 leading-relaxed italic ${editClass}`}
                                                    contentEditable={isEditing}
                                                    suppressContentEditableWarning
                                                    onBlur={(e) => onEdit('testimonials', 'text', e.currentTarget.textContent?.replace(/^"|"$/g, '') || '', idx)}
                                                >
                                                    &ldquo;{testimonial.text}&rdquo;
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Dots */}
                    {testimonials.length > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-8">
                            {testimonials.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setActiveIndex(idx)}
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        idx === activeIndex ? 'w-8' : 'w-2 bg-gray-300 hover:bg-gray-400'
                                    }`}
                                    style={idx === activeIndex ? { backgroundColor: primaryColor } : {}}
                                    aria-label={`View testimonial ${idx + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 4: WALL OF LOVE
// ============================================================================
function TestimonialsWall({
    testimonials, title, primaryColor, isEditing, onEdit, sizeClasses
}: Omit<TestimonialsSectionProps, 'layout' | 'layoutConfig'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    // Split testimonials into columns for masonry effect
    const getColumns = (items: typeof testimonials, numCols: number) => {
        const cols: (typeof testimonials)[] = Array.from({ length: numCols }, () => []);
        items.forEach((item, idx) => {
            cols[idx % numCols].push(item);
        });
        return cols;
    };

    // Use indices mapping to track original index for editing
    const getColumnIndices = (items: typeof testimonials, numCols: number) => {
        const cols: number[][] = Array.from({ length: numCols }, () => []);
        items.forEach((_, idx) => {
            cols[idx % numCols].push(idx);
        });
        return cols;
    };

    // Use masonry columns only if we have enough items, otherwise use a simpler centered layout
    const numCols = testimonials.length >= 3 ? 3 : testimonials.length;
    const columns = getColumns(testimonials, numCols);
    const columnIndices = getColumnIndices(testimonials, numCols);

    // Grid class adapts to actual number of columns needed
    const gridClass = testimonials.length >= 3
        ? 'grid grid-cols-1 @md:grid-cols-2 @xl:grid-cols-3 gap-4'
        : testimonials.length === 2
            ? 'grid grid-cols-1 @md:grid-cols-2 gap-4 max-w-3xl mx-auto'
            : 'grid grid-cols-1 gap-4 max-w-md mx-auto';

    return (
        <section id="testimonials" className="py-20 px-6 bg-white @container">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-14">
                    <h2
                        className={`${sizeClasses.heading} font-bold text-gray-900 mb-3 ${editClass}`}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={(e) => onEdit('testimonials', 'title', e.currentTarget.textContent || '')}
                    >
                        {title || 'Wall of Love'}
                    </h2>
                    <p className={`${sizeClasses.subheading} text-gray-500`}>
                        Hear from people who love what we&apos;re building
                    </p>
                </div>

                {/* Masonry Grid - centered */}
                <div className={gridClass}>
                    {columns.map((column, colIdx) => (
                        <div key={colIdx} className="flex flex-col gap-4">
                            {column.map((testimonial, rowIdx) => {
                                const originalIdx = columnIndices[colIdx][rowIdx];
                                return (
                                    <div
                                        key={originalIdx}
                                        className="p-5 rounded-2xl border border-gray-200 bg-gray-50/50 hover:bg-white hover:shadow-md transition-all duration-300 text-center"
                                    >
                                        {/* Avatar centered */}
                                        <div className="flex flex-col items-center mb-3">
                                            <Avatar name={testimonial.author} primaryColor={primaryColor} size="sm" />
                                            <p
                                                className={`text-sm font-semibold text-gray-900 mt-2 ${editClass}`}
                                                contentEditable={isEditing}
                                                suppressContentEditableWarning
                                                onBlur={(e) => onEdit('testimonials', 'author', e.currentTarget.textContent || '', originalIdx)}
                                            >
                                                {testimonial.author}
                                            </p>
                                            <p
                                                className={`text-xs text-gray-500 ${editClass}`}
                                                contentEditable={isEditing}
                                                suppressContentEditableWarning
                                                onBlur={(e) => onEdit('testimonials', 'role', e.currentTarget.textContent || '', originalIdx)}
                                            >
                                                {testimonial.role}
                                            </p>
                                            <div className="flex items-center gap-0.5 mt-1">
                                                {[...Array(5)].map((_, i) => (
                                                    <Icon key={i} name="StarIcon" className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Quote */}
                                        <p
                                            className={`text-sm text-gray-700 leading-relaxed ${editClass}`}
                                            contentEditable={isEditing}
                                            suppressContentEditableWarning
                                            onBlur={(e) => onEdit('testimonials', 'text', e.currentTarget.textContent?.replace(/^"|"$/g, '') || '', originalIdx)}
                                        >
                                            &ldquo;{testimonial.text}&rdquo;
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ============================================================================
// MAIN EXPORT WITH LAYOUT SWITCHING
// ============================================================================
export function TestimonialsSection({
    testimonials,
    title,
    primaryColor,
    isEditing,
    onEdit,
    sizeClasses,
    layout = 'cards',
    layoutConfig
}: TestimonialsSectionProps) {
    const commonProps = { testimonials, title, primaryColor, isEditing, onEdit, sizeClasses };

    switch (layout) {
        case 'spotlight':
            return <TestimonialsSpotlight {...commonProps} />;
        case 'carousel':
            return <TestimonialsCarousel {...commonProps} />;
        case 'wall':
            return <TestimonialsWall {...commonProps} />;
        case 'cards':
        default:
            return <TestimonialsCards {...commonProps} />;
    }
}
